import { useContext } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import { handleRequest } from '../quipli-service-client';
import {
  CustomerWorkOrderRequest,
  FleetWorkOrderRequest,
  ScheduledService,
  ScheduledServiceRequest,
  UpdateWorkOrderRequest,
  UpdateWorkOrderStatusRequest,
  WorkOrder,
  WorkOrderExportRequest,
  WorkOrderProductInput,
  WorkOrderSearchRequest,
  WorkOrderType,
} from './service-and-repair';
import { ScheduledServiceParsers, WorkOrderParsers } from './service-and-repair-parsers';
import { Pagination } from '../pagination/pagination';
import { PaginationParsers } from '../pagination/pagination-parsers';
import { AxiosRequestConfig } from 'axios';

export const useServiceAndRepairAPI = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  async function listWorkOrderTypes(): Promise<WorkOrderType[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/repair/work-orders/types`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleRequest<WorkOrderParsers.WorkOrderTypeJson[]>(config);
    return result.map(WorkOrderParsers.toWorkOrderType);
  }

  async function searchWorkOrders(request: WorkOrderSearchRequest): Promise<Pagination<WorkOrder>> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/repair/work-orders/search`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };
    const { items, pagination } = await handleRequest<PaginationParsers.PaginationJson<WorkOrderParsers.WorkOrderJson>>(
      config,
    );
    return {
      pagination,
      items: items.map(WorkOrderParsers.toWorkOrder),
    };
  }

  async function exportWorkOrders(request: WorkOrderExportRequest): Promise<BlobPart> {
    const config: AxiosRequestConfig = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/repair/work-orders/export`,
      headers: {
        'X-Tenant': tenant,
        Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      },
      data: request,
      responseType: 'blob',
    };
    return await handleRequest(config, true);
  }

  async function getWorkOrderById(workOrderId: string): Promise<WorkOrder> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/repair/work-orders/${workOrderId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const wo = await handleRequest<WorkOrderParsers.WorkOrderJson>(config);
    return WorkOrderParsers.toWorkOrder(wo);
  }

  async function submitFleetWorkOrder(request: FleetWorkOrderRequest): Promise<WorkOrder> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/repair/work-orders/fleet`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };

    const result = await handleRequest<WorkOrderParsers.WorkOrderJson>(config);
    return WorkOrderParsers.toWorkOrder(result);
  }

  async function submitCustomerWorkOrder(request: CustomerWorkOrderRequest): Promise<WorkOrder> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/repair/work-orders/customer`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };

    const result = await handleRequest<WorkOrderParsers.WorkOrderJson>(config);
    return WorkOrderParsers.toWorkOrder(result);
  }

  async function updateWorkOrder(request: UpdateWorkOrderRequest) {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/repair/work-orders/${request.id}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };

    const result = await handleRequest<WorkOrderParsers.WorkOrderJson>(config);
    return WorkOrderParsers.toWorkOrder(result);
  }

  async function createWorkOrderProduct(workOrderId: string, input: WorkOrderProductInput) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/repair/work-orders/${workOrderId}/products`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        productId: input.productId,
        customerUnitPrice: input.customerUnitPrice,
        quantity: input.quantity,
        description: input.description,
      },
    };

    const result = await handleRequest<WorkOrderParsers.WorkOrderJson>(config);
    return WorkOrderParsers.toWorkOrder(result);
  }

  async function updateWorkOrderProduct(workOrderId: string, input: WorkOrderProductInput) {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/repair/work-orders/${workOrderId}/products/${input.id}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        id: input.id,
        productId: input.productId,
        customerUnitPrice: input.customerUnitPrice,
        quantity: input.quantity,
        description: input.description,
      },
    };

    const result = await handleRequest<WorkOrderParsers.WorkOrderJson>(config);
    return WorkOrderParsers.toWorkOrder(result);
  }

  async function deleteWorkOrderProduct(workOrderId: string, workOrderProductId: string) {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/v1/repair/work-orders/${workOrderId}/products/${workOrderProductId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<WorkOrderParsers.WorkOrderJson>(config);
    return WorkOrderParsers.toWorkOrder(result);
  }

  async function updateWorkOrderStatus(request: UpdateWorkOrderStatusRequest) {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/repair/work-orders/${request.id}/status`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };

    const result = await handleRequest<WorkOrderParsers.WorkOrderJson>(config);
    return WorkOrderParsers.toWorkOrder(result);
  }

  async function getWorkOrderNoteFiles(workOrderId: string) {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/repair/work-orders/${workOrderId}/noteFiles`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<WorkOrderParsers.WorkOrderNoteFileJson[]>(config);
    return result.map(WorkOrderParsers.toWorkOrderAttachment);
  }

  async function addWorkOrderNoteFile(workOrderId: string, uploadedBy: number, note?: string, file?: string) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/repair/work-orders/${workOrderId}/noteFiles`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        workOrderId,
        note,
        file,
        uploadedBy,
      },
    };

    const result = await handleRequest<WorkOrderParsers.WorkOrderNoteFileJson>(config);
    return WorkOrderParsers.toWorkOrderAttachment(result);
  }

  async function getWorkOrderFileUploadUrl(workOrderId: string, file: string) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/repair/work-orders/${workOrderId}/files/${file}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<string>(config);
  }

  async function putWorkOrderFile(url: string, file: File) {
    const config = {
      method: 'put',
      url: url,
      data: file,
      headers: {
        'Content-Type': file.type,
      },
    };

    return await handleRequest<string>(config);
  }

  async function downloadWorkOrderFile(workOrderId: string, objectId: string) {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/repair/work-orders/${workOrderId}/files/${objectId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<string>(config);
  }

  async function deleteWorkOrderNoteFile(workOrderId: string, objectId: string) {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/v1/repair/work-orders/${workOrderId}/noteFiles/${objectId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<number>(config);
  }

  async function updateWorkOrderNote(workOrderId: string, objectId: string, note?: string) {
    const config = {
      method: 'patch',
      url: `${contextPath}quipli-service/v1/repair/work-orders/${workOrderId}/noteFiles/${objectId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        note,
      },
    };

    return await handleRequest<number>(config);
  }

  async function searchScheduledServices({
    pageSize,
    pageNumber,
    keyword,
  }: {
    pageSize: number;
    pageNumber: number;
    keyword?: string;
  }): Promise<Pagination<ScheduledService>> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/repair/scheduled-services?pageSize=${pageSize}&pageNumber=${pageNumber}${
        keyword ? `&keyword=${keyword}` : ''
      }`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const { items, pagination } = await handleRequest<
      PaginationParsers.PaginationJson<ScheduledServiceParsers.ScheduledServiceJson>
    >(config);
    return {
      pagination,
      items: items.map(ScheduledServiceParsers.toScheduledService),
    };
  }

  async function createScheduledService(req: ScheduledServiceRequest): Promise<ScheduledService> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/repair/scheduled-services`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { ...req, unitStatus: req.unitStatus.replace(/\s/g, '') },
    };
    const res = await handleRequest<ScheduledServiceParsers.ScheduledServiceJson>(config);
    return ScheduledServiceParsers.toScheduledService(res);
  }

  async function getScheduledService(id: string): Promise<ScheduledService> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/repair/scheduled-services/${id}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const res = await handleRequest<ScheduledServiceParsers.ScheduledServiceJson>(config);
    return ScheduledServiceParsers.toScheduledService(res);
  }

  async function updateScheduledService(id: string, req: ScheduledServiceRequest): Promise<ScheduledService> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/repair/scheduled-services/${id}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { ...req, unitStatus: req.unitStatus.toString().replace(/\s/g, '') },
    };
    const res = await handleRequest<ScheduledServiceParsers.ScheduledServiceJson>(config);
    return ScheduledServiceParsers.toScheduledService(res);
  }

  async function deleteScheduledService(id: string) {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/v1/repair/scheduled-services/${id}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest(config);
  }

  return {
    listWorkOrderTypes,
    searchWorkOrders,
    exportWorkOrders,
    getWorkOrderById,
    submitFleetWorkOrder,
    submitCustomerWorkOrder,
    updateWorkOrder,
    createWorkOrderProduct,
    updateWorkOrderProduct,
    deleteWorkOrderProduct,
    updateWorkOrderStatus,
    downloadWorkOrderFile,
    getWorkOrderNoteFiles,
    addWorkOrderNoteFile,
    getWorkOrderFileUploadUrl,
    putWorkOrderFile,
    deleteWorkOrderNoteFile,
    updateWorkOrderNote,
    searchScheduledServices,
    createScheduledService,
    updateScheduledService,
    deleteScheduledService,
    getScheduledService,
  };
};
